import * as THREE from 'three'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import { submitSceneInView } from '@/plugins/shared/submitSceneInView'
import {
  createFillMaterial,
  createLineMaterial,
  TOOL_COLOR_CLIP,
} from '@/plugins/tools/shared/ToolMaterials'
import {
  getActiveViewId,
  getOptionalViewRegistry,
  getViewToolsRoot,
} from '@/plugins/tools/shared/viewScope'
import type { EngineContext } from '@/runtime/EngineContext'
import { ClipTask } from '@/shared/constants'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type { ClipSelectionMode, Disposable } from '@/shared/types/core'
import type { Plugin } from '@/shared/types/plugin'
import type {
  ClipService,
  RenderContributor,
  RenderPipeline,
  ViewRegistry,
} from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import type { ClipPlugin } from './ClipPlugin'

const WIREFRAME_POSITIONS = new Float32Array([
  -0.5, -0.5, -0.5, 0.5, -0.5, -0.5, 0.5, -0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5, -0.5, -0.5,
  0.5, -0.5, -0.5, 0.5, -0.5, -0.5, -0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5,
  0.5, 0.5, 0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5, 0.5, -0.5, 0.5, -0.5, -0.5, -0.5, -0.5, -0.5, 0.5,
  -0.5, 0.5, -0.5, -0.5, 0.5, 0.5, -0.5, 0.5, -0.5, 0.5, 0.5, 0.5, 0.5, -0.5, -0.5, 0.5, -0.5, 0.5,
  0.5,
])

function clonePoints(points: ReadonlyArray<THREE.Vector3>): THREE.Vector3[] {
  return points.map((point) => point.clone())
}

function buildRectanglePoints(start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3[] {
  return [
    new THREE.Vector3(start.x, start.y, 0),
    new THREE.Vector3(end.x, start.y, 0),
    new THREE.Vector3(end.x, end.y, 0),
    new THREE.Vector3(start.x, end.y, 0),
  ]
}

function disposeObjectTree(root: THREE.Object3D): void {
  root.traverse((child) => {
    const renderable = child as THREE.Mesh | THREE.Line | THREE.LineSegments | THREE.Points
    renderable.geometry?.dispose()

    const materials = Array.isArray(renderable.material)
      ? renderable.material
      : renderable.material
        ? [renderable.material]
        : []

    for (const material of materials) {
      material.dispose()
    }
  })

  while (root.children.length > 0) {
    root.remove(root.children[0])
  }
}

function createBoxVisual(matrix: THREE.Matrix4, fillOpacity: number): THREE.Group {
  const group = new THREE.Group()
  group.matrixAutoUpdate = false
  group.matrix.copy(matrix)

  const wireGeometry = new THREE.BufferGeometry()
  wireGeometry.setAttribute('position', new THREE.BufferAttribute(WIREFRAME_POSITIONS.slice(), 3))
  const wireframe = new THREE.LineSegments(wireGeometry, createLineMaterial(TOOL_COLOR_CLIP))
  group.add(wireframe)

  const fill = new THREE.Mesh(
    new THREE.BoxGeometry(1, 1, 1),
    createFillMaterial(TOOL_COLOR_CLIP, fillOpacity)
  )
  group.add(fill)

  return group
}

function createPolygonVisual(
  vertices: ReadonlyArray<THREE.Vector3>,
  fillOpacity: number
): THREE.Group {
  const group = new THREE.Group()
  if (vertices.length === 0) {
    return group
  }

  if (vertices.length === 1) {
    group.add(createVertexPoints(vertices, 10))
    return group
  }

  const linePositions = new Float32Array((vertices.length + 1) * 3)
  for (let index = 0; index < vertices.length; index++) {
    const point = vertices[index]
    linePositions[index * 3] = point.x
    linePositions[index * 3 + 1] = point.y
    linePositions[index * 3 + 2] = point.z
  }

  linePositions[vertices.length * 3] = vertices[0].x
  linePositions[vertices.length * 3 + 1] = vertices[0].y
  linePositions[vertices.length * 3 + 2] = vertices[0].z

  const lineGeometry = new THREE.BufferGeometry()
  lineGeometry.setAttribute('position', new THREE.BufferAttribute(linePositions, 3))
  group.add(new THREE.Line(lineGeometry, createLineMaterial(TOOL_COLOR_CLIP)))
  group.add(createVertexPoints(vertices, 8))

  if (vertices.length >= 3) {
    const fillPositions = new Float32Array((vertices.length - 2) * 9)
    for (let index = 0; index < vertices.length - 2; index++) {
      const base = index * 9
      fillPositions[base] = vertices[0].x
      fillPositions[base + 1] = vertices[0].y
      fillPositions[base + 2] = vertices[0].z
      fillPositions[base + 3] = vertices[index + 1].x
      fillPositions[base + 4] = vertices[index + 1].y
      fillPositions[base + 5] = vertices[index + 1].z
      fillPositions[base + 6] = vertices[index + 2].x
      fillPositions[base + 7] = vertices[index + 2].y
      fillPositions[base + 8] = vertices[index + 2].z
    }

    const fillGeometry = new THREE.BufferGeometry()
    fillGeometry.setAttribute('position', new THREE.BufferAttribute(fillPositions, 3))
    group.add(new THREE.Mesh(fillGeometry, createFillMaterial(TOOL_COLOR_CLIP, fillOpacity)))
  }

  return group
}

function createOpenPolylineVisual(vertices: ReadonlyArray<THREE.Vector3>): THREE.Group {
  const group = new THREE.Group()

  if (vertices.length === 0) {
    return group
  }

  group.add(createVertexPoints(vertices, 8))

  if (vertices.length < 2) {
    return group
  }

  const positions = new Float32Array(vertices.length * 3)
  for (let index = 0; index < vertices.length; index++) {
    const point = vertices[index]
    positions[index * 3] = point.x
    positions[index * 3 + 1] = point.y
    positions[index * 3 + 2] = point.z
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  group.add(new THREE.Line(geometry, createLineMaterial(TOOL_COLOR_CLIP)))

  return group
}

function createVertexPoints(vertices: ReadonlyArray<THREE.Vector3>, size: number): THREE.Points {
  const geometry = new THREE.BufferGeometry()
  const positions = new Float32Array(vertices.length * 3)

  for (let index = 0; index < vertices.length; index++) {
    const point = vertices[index]
    positions[index * 3] = point.x
    positions[index * 3 + 1] = point.y
    positions[index * 3 + 2] = point.z
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))

  return new THREE.Points(
    geometry,
    new THREE.PointsMaterial({
      color: TOOL_COLOR_CLIP,
      size,
      sizeAttenuation: false,
      depthTest: false,
      transparent: true,
      opacity: 0.95,
    })
  )
}

function getDraftScreenVertices(
  selectionMode: ClipSelectionMode,
  points: ReadonlyArray<THREE.Vector3>,
  previewPoint: THREE.Vector3 | null
): THREE.Vector3[] {
  if (selectionMode === 'rectangle') {
    if (points.length >= 2) {
      return buildRectanglePoints(points[0], points[1])
    }

    if (points.length === 1 && previewPoint) {
      return buildRectanglePoints(points[0], previewPoint)
    }

    return clonePoints(points)
  }

  const vertices = clonePoints(points)
  if (previewPoint && points.length > 0) {
    vertices.push(previewPoint.clone())
  }

  return vertices
}

/**
 * 剪裁盒/多边形覆盖渲染插件，将 ClipService 的几何可视化到 Overlay3D 阶段。
 */
export class ClipOverlayPlugin implements Plugin, RenderContributor {
  /** 插件名称 */
  readonly name = 'clip-overlay'
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 55
  /** 依赖的服务名列表 */
  readonly dependencies = ['ClipService', 'RenderPipeline'] as const

  /** 渲染贡献者类型标识 */
  readonly kind = 'clip-overlay'
  /** 需要参与的渲染阶段 */
  readonly phases: readonly RenderPhase[] = [RenderPhase.Overlay3D]
  /** 渲染优先级，数值越小越先绘制 */
  readonly renderPriority = 0

  private context!: EngineContext
  private clipService!: ClipService & Pick<ClipPlugin, 'getBoxGizmoHelper' | 'isActive'>
  private pipeline!: RenderPipeline
  private changedSub: Disposable | null = null
  private viewRegistry: ViewRegistry | null = null

  private readonly root = new THREE.Group()
  private readonly confirmedRoot = new THREE.Group()
  private readonly helperRoot = new THREE.Group()
  private readonly screenRoot = new THREE.Group()
  private readonly draftRoot = new THREE.Group()
  private readonly overlayScene = new THREE.Scene()
  private readonly screenScene = new THREE.Scene()
  private readonly screenCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, -1, 1)

  private confirmedDirty = true
  private confirmedViewId: string | null = null
  private draftDirty = true
  private draftViewId: string | null = null
  private attachedHelper: THREE.Object3D | null = null

  constructor() {
    this.root.name = 'clipOverlayRoot'
    this.confirmedRoot.name = 'clipConfirmedRoot'
    this.draftRoot.name = 'clipDraftRoot'
    this.helperRoot.name = 'clipHelperRoot'
    this.screenRoot.name = 'clipScreenRoot'
    this.root.add(this.confirmedRoot, this.helperRoot)
    this.screenRoot.add(this.draftRoot)
  }

  /**
   * 初始化插件，缓存 EngineContext。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
  }

  /** 启动插件，解析依赖并挂载 overlay 根节点到场景 */
  onStart(): void {
    this.clipService = this.context.getService('ClipService') as ClipService &
      Pick<ClipPlugin, 'getBoxGizmoHelper' | 'isActive'>
    this.pipeline = this.context.getService('RenderPipeline')
    this.viewRegistry = getOptionalViewRegistry(this.context)
    this.pipeline.addRenderContributor(this)
    this.resolveToolsRoot(getActiveViewId(this.viewRegistry)).add(this.root)
    this.resolveToolsRoot(getActiveViewId(this.viewRegistry)).add(this.screenRoot)

    this.changedSub = this.clipService.onChanged(() => {
      this.confirmedDirty = true
      this.draftDirty = true
    })
  }

  /** 销毁插件，移除 overlay 并释放几何资源 */
  onDestroy(): void {
    this.changedSub?.dispose()
    this.changedSub = null
    this.pipeline.removeRenderContributor(this)
    this.syncHelper(null)
    disposeObjectTree(this.confirmedRoot)
    disposeObjectTree(this.draftRoot)
    this.root.removeFromParent()
    this.screenRoot.removeFromParent()
    this.confirmedViewId = null
    this.viewRegistry = null
  }

  /**
   * 渲染剪裁覆盖层。
   *
   * @param frame - 帧上下文
   * @param phase - 当前渲染阶段，仅处理 Overlay3D
   * @param ctx - 当前阶段渲染上下文
   */
  render(frame: ViewFrameContext, phase: RenderPhase, ctx: PhaseRenderContext): void {
    if (phase !== RenderPhase.Overlay3D) return

    const activeViewId = getActiveViewId(this.viewRegistry)
    const toolsRoot = this.resolveToolsRoot(ctx.viewId)
    if (this.root.parent !== toolsRoot) {
      this.root.parent?.remove(this.root)
      toolsRoot.add(this.root)
    }
    if (this.screenRoot.parent !== toolsRoot) {
      this.screenRoot.parent?.remove(this.screenRoot)
      toolsRoot.add(this.screenRoot)
    }

    if (this.confirmedDirty || this.confirmedViewId !== ctx.viewId) {
      this.rebuildConfirmedVisuals(ctx.viewId)
      this.confirmedDirty = false
      this.confirmedViewId = ctx.viewId
    }

    if (!activeViewId || ctx.viewId === activeViewId) {
      if (this.draftDirty || this.draftViewId !== ctx.viewId) {
        this.rebuildDraftVisuals()
        this.draftDirty = false
        this.draftViewId = ctx.viewId
      }
      this.syncHelper(this.clipService.isActive ? this.clipService.getBoxGizmoHelper() : null)
    } else {
      disposeObjectTree(this.draftRoot)
      this.syncHelper(null)
      this.draftViewId = null
    }

    if (
      this.confirmedRoot.children.length === 0 &&
      this.draftRoot.children.length === 0 &&
      this.helperRoot.children.length === 0
    ) {
      return
    }

    // viewport/scissor 与 output target 由 backend 依当前 View 决定，这里只提交场景。
    if (this.confirmedRoot.children.length > 0 || this.helperRoot.children.length > 0) {
      this.overlayScene.children.length = 0
      this.overlayScene.add(this.root)
      submitSceneInView(ctx, this.overlayScene, frame.camera)
    }

    if (this.draftRoot.children.length > 0) {
      this.screenScene.children.length = 0
      this.screenScene.add(this.screenRoot)
      submitSceneInView(ctx, this.screenScene, this.screenCamera)
    }

    toolsRoot.add(this.root)
    toolsRoot.add(this.screenRoot)
  }

  private resolveToolsRoot(viewId: string | null): THREE.Object3D {
    return getViewToolsRoot(this.context, this.viewRegistry, viewId)
  }

  private rebuildConfirmedVisuals(viewId: string): void {
    disposeObjectTree(this.confirmedRoot)

    for (const volume of this.clipService.volumes) {
      if (
        volume.clipTask === ClipTask.HIGHLIGHT ||
        volume.type !== 'box' ||
        volume.selectionMode === 'box3d' ||
        (volume.viewId != null && volume.viewId !== viewId)
      ) {
        continue
      }

      this.confirmedRoot.add(createBoxVisual(volume.matrix, 0.16))
    }
  }

  private rebuildDraftVisuals(): void {
    disposeObjectTree(this.draftRoot)

    if (!this.clipService.isActive) {
      return
    }

    const draft = this.clipService.getDraftState()

    if (draft.selectionMode === 'box3d') {
      return
    }

    const screenVertices = getDraftScreenVertices(
      draft.selectionMode,
      draft.points,
      draft.previewPoint
    )

    if (screenVertices.length === 0 && draft.previewPoint) {
      this.draftRoot.add(createVertexPoints([draft.previewPoint.clone()], 10))
      return
    }

    if (draft.selectionMode === 'rectangle' && screenVertices.length >= 4) {
      this.draftRoot.add(createPolygonVisual(screenVertices, 0.06))
      return
    }

    if (draft.selectionMode === 'polygon' && screenVertices.length >= 3) {
      this.draftRoot.add(createPolygonVisual(screenVertices, 0.04))
      return
    }

    this.draftRoot.add(createOpenPolylineVisual(screenVertices))
  }

  private syncHelper(nextHelper: THREE.Object3D | null): void {
    if (this.attachedHelper === nextHelper) {
      return
    }

    if (this.attachedHelper && this.attachedHelper.parent === this.helperRoot) {
      this.helperRoot.remove(this.attachedHelper)
    }

    this.attachedHelper = nextHelper

    if (nextHelper) {
      this.helperRoot.add(nextHelper)
    }
  }
}

export default ClipOverlayPlugin
